import { PathologyLab } from '../types';

/**
 * Deterministic pseudo-random generator using index as seed
 * This ensures consistent results for the same lab index
 */
const seededRandom = (index: number, salt: number): number => {
  const x = Math.sin(index * salt) * 10000;
  return x - Math.floor(x);
};

// Deterministic random in range
const seededRandomInRange = (index: number, salt: number, min: number, max: number): number =>
  min + seededRandom(index, salt) * (max - min);

// States in Southeast
const states = ['NC', 'SC', 'GA', 'FL', 'AL', 'TN', 'MS', 'LA', 'AR', 'VA'];
const regions = [
  'NC/SC/GA', 'Southeast', 'Florida', 'Alabama', 'Tennessee',
  'Mississippi', 'Louisiana', 'Arkansas', 'Virginia', 'Carolinas'
];

// Generate a single pathology lab deterministically
const generateLab = (index: number): PathologyLab => {
  const revenue = seededRandomInRange(index, 101, 0.5, 12); // $0.5M to $12M
  const multiLocation = seededRandom(index, 102) > 0.6;
  const ownerAge = seededRandom(index, 103) > 0.4
    ? Math.floor(seededRandomInRange(index, 104, 55, 72))
    : undefined;

  // Calculate platform vs rollup fit based on characteristics
  const platformScore = Math.min(100, Math.max(0,
    (revenue >= 1.5 && revenue <= 9 ? 40 : 0) +
    (multiLocation ? 20 : 0) +
    (seededRandom(index, 105) * 40)
  ));

  const rollupScore = Math.min(100, Math.max(0,
    (revenue >= 1 && revenue <= 5 ? 40 : 0) +
    (!multiLocation ? 20 : 0) +
    (ownerAge && ownerAge >= 60 ? 15 : 0) +
    (seededRandom(index, 106) * 25)
  ));

  // Deterministic coordinates in Southeast US
  const longitude = seededRandomInRange(index, 107, -85, -75);
  const latitude = seededRandomInRange(index, 108, 30, 37);

  return {
    id: `lab-${index}`,
    name: `Lab P-${index}`,
    revenue: parseFloat(revenue.toFixed(2)),
    location: {
      state: states[Math.floor(seededRandom(index, 109) * states.length)],
      region: regions[Math.floor(seededRandom(index, 110) * regions.length)],
      coordinates: [longitude, latitude],
    },
    characteristics: {
      multiLocation,
      locationsCount: multiLocation ? Math.floor(seededRandomInRange(index, 111, 2, 5)) : 1,
      regionalPresence: multiLocation && seededRandom(index, 112) > 0.5 ? 'multi-state' : 'single-state',
      ownerAge,
      managementTransition: seededRandom(index, 113) > 0.5,
      payorContracts: Math.floor(seededRandomInRange(index, 114, 3, 20)),
      techInfrastructure: ['basic', 'moderate', 'advanced'][Math.floor(seededRandom(index, 115) * 3)] as 'basic' | 'moderate' | 'advanced',
    },
    scores: {
      platformFit: Math.floor(platformScore),
      rollupFit: Math.floor(rollupScore),
    },
    path: 'pending',
  };
};

// Strategic lab indices (deterministic selection for consistency)
// 3 Platform candidates - geographically diverse
// 247: NC/SC/GA region (~$8.6M revenue)
// 1089: Florida
// 2567: Tennessee
const PLATFORM_INDICES = [247, 1089, 2567];

// 72 Add-on candidates - distributed across the dataset
const ADDON_INDICES = [
  12, 34, 67, 89, 123, 156, 189, 234, 267, 289,
  312, 345, 378, 401, 434, 467, 490, 523, 556, 589,
  612, 645, 678, 701, 734, 767, 790, 823, 856, 889,
  912, 945, 978, 1001, 1034, 1067, 1090, 1123, 1156, 1189,
  1212, 1245, 1278, 1301, 1334, 1367, 1390, 1423, 1456, 1489,
  1512, 1545, 1578, 1601, 1634, 1667, 1690, 1723, 1756, 1789,
  1812, 1845, 1878, 1901, 1934, 1967, 1990, 2023, 2056, 2089,
  2112, 2145
];

/**
 * Target filter distributions (realistic funnel)
 * These percentages create a believable narrowing effect
 */
const FILTER_TARGETS = {
  // Platform criteria
  revenuePlatform: 850,       // 15.7% - Revenue $1.5M-$9M
  multiLocation: 450,         // 8.3% - Multi-location
  regionalPresence: 280,      // 5.2% - Multi-state presence
  managementTransition: 320,  // 5.9% - Owner transition ready
  payorDiversity: 400,        // 7.4% - Strong payor mix
  techInfrastructure: 520,    // 9.6% - Good tech
  growthPotential: 180,       // 3.3% - High platform fit

  // Add-on criteria
  revenueRollup: 920,         // 17.0% - Revenue $1M-$5M
  singleDualLocation: 1100,   // 20.3% - 1-2 locations
  specialtyFocus: 650,        // 12.0% - Specialty focus
  agingOwnership: 580,        // 10.7% - Owner 60+
  regionalStrength: 480,      // 8.9% - Regional strength
  stableReferrals: 550,       // 10.2% - Stable referrals
};

/**
 * Calculate filter flags for a lab based on its index
 * Uses deterministic logic to create realistic overlaps
 */
const calculateFilterFlags = (index: number, totalLabs: number) => {
  // Helper to determine if lab meets a criterion based on target count
  const meetsCriterion = (targetCount: number, salt: number, correlation: number = 0): boolean => {
    const baseThreshold = targetCount / totalLabs;
    const random = seededRandom(index, salt) + correlation;
    return random < baseThreshold;
  };

  // Calculate base flags
  const revenuePlatform = meetsCriterion(FILTER_TARGETS.revenuePlatform, 1001);
  const multiLocation = meetsCriterion(FILTER_TARGETS.multiLocation, 2003);
  const regionalPresence = meetsCriterion(FILTER_TARGETS.regionalPresence, 3007, multiLocation ? 0.15 : 0);
  const managementTransition = meetsCriterion(FILTER_TARGETS.managementTransition, 4019);
  const payorDiversity = meetsCriterion(FILTER_TARGETS.payorDiversity, 5021);
  const techInfrastructure = meetsCriterion(FILTER_TARGETS.techInfrastructure, 6029, multiLocation ? 0.1 : 0);
  const growthPotential = meetsCriterion(FILTER_TARGETS.growthPotential, 7039,
    (revenuePlatform && multiLocation) ? 0.2 : 0);

  const revenueRollup = meetsCriterion(FILTER_TARGETS.revenueRollup, 8053);
  const singleDualLocation = meetsCriterion(FILTER_TARGETS.singleDualLocation, 9067);
  const specialtyFocus = meetsCriterion(FILTER_TARGETS.specialtyFocus, 10079);
  const agingOwnership = meetsCriterion(FILTER_TARGETS.agingOwnership, 11087, managementTransition ? 0.25 : 0);
  const regionalStrength = meetsCriterion(FILTER_TARGETS.regionalStrength, 12097);
  const stableReferrals = meetsCriterion(FILTER_TARGETS.stableReferrals, 13103, regionalStrength ? 0.15 : 0);

  return {
    revenuePlatform,
    multiLocation,
    regionalPresence,
    managementTransition,
    payorDiversity,
    techInfrastructure,
    growthPotential,
    revenueRollup,
    singleDualLocation,
    specialtyFocus,
    agingOwnership,
    regionalStrength,
    stableReferrals,
  };
};

// Generate all 5,414 labs
export const generatePathologyLabs = (): PathologyLab[] => {
  const labs: PathologyLab[] = [];
  const totalLabs = 5414;

  for (let i = 1; i <= totalLabs; i++) {
    const lab = generateLab(i);

    // Calculate filter flags deterministically
    let filterFlags = calculateFilterFlags(i, totalLabs);

    // Assign strategic classifications
    const isPlatform = PLATFORM_INDICES.includes(i);
    const isAddon = ADDON_INDICES.includes(i);

    if (isPlatform) {
      lab.labType = 'platform';
      // Platform candidates should meet most/all platform criteria
      filterFlags = {
        ...filterFlags,
        revenuePlatform: true,
        multiLocation: true,
        regionalPresence: true,
        managementTransition: true,
        payorDiversity: true,
        techInfrastructure: true,
        growthPotential: true,
      };
    } else if (isAddon) {
      lab.labType = 'addon';
      // Add-on candidates should meet most add-on criteria
      filterFlags = {
        ...filterFlags,
        revenueRollup: true,
        singleDualLocation: true,
        specialtyFocus: true,
        agingOwnership: true,
        regionalStrength: true,
        stableReferrals: true,
      };
    }

    // Assign filter flags to lab
    lab.filterFlags = filterFlags;

    // Override specific platform lab characteristics
    if (i === 247) {
      // NC/SC/GA platform
      lab.revenue = 8.6;
      lab.location.state = 'NC';
      lab.location.region = 'NC/SC/GA';
      lab.characteristics.ownerAge = 62;
      lab.characteristics.techInfrastructure = 'advanced';
    } else if (i === 1089) {
      // Florida platform
      lab.location.state = 'FL';
      lab.location.region = 'Florida';
    } else if (i === 2567) {
      // Tennessee platform
      lab.location.state = 'TN';
      lab.location.region = 'Tennessee';
    }

    labs.push(lab);
  }
  return labs;
};

// For development, we can use a smaller subset
export const pathologyLabs = generatePathologyLabs();

// Export a sample for quick testing
export const sampleLabs = pathologyLabs.slice(0, 100);
